import React, { useState } from 'react';
import { Button, Divider, Dropdown, Input, message, Popover, Space, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
  BoldOutlined,
  CheckSquareOutlined,
  CodeOutlined,
  FontSizeOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RedoOutlined,
  StrikethroughOutlined,
  TableOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';

/** Mirrors the editor's own `isAllowedUri` guard: only these two schemes may be stored. */
const SAFE_LINK_SCHEME = /^(https?:\/\/|mailto:)/i;

/** `example.com`, `www.example.com/path` — a host without a scheme, which we make https. */
const BARE_DOMAIN = /^[\w-]+(\.[\w-]+)+(?::\d+)?([/?#]|$)/;

export type EditorToolbarProps = {
  editor: Editor;
  /** Opens the hidden file input owned by the editor. */
  onPickImage: () => void;
};

type ToolButtonProps = {
  title: string;
  icon: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
};

/** `onMouseDown` is swallowed so clicking a button never takes the selection out of the editor. */
const ToolButton: React.FC<ToolButtonProps> = ({ title, icon, active, disabled, onClick }) => (
  <Tooltip title={title}>
    <Button
      type="text"
      size="small"
      icon={icon}
      disabled={disabled}
      className={active ? 'editor-toolbar-button is-active' : 'editor-toolbar-button'}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    />
  </Tooltip>
);

const EditorToolbar: React.FC<EditorToolbarProps> = ({ editor, onPickImage }) => {
  const { t } = useTranslation();
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      bold: instance.isActive('bold'),
      italic: instance.isActive('italic'),
      underline: instance.isActive('underline'),
      strike: instance.isActive('strike'),
      code: instance.isActive('code'),
      heading: instance.isActive('heading'),
      bulletList: instance.isActive('bulletList'),
      orderedList: instance.isActive('orderedList'),
      taskList: instance.isActive('taskList'),
      blockquote: instance.isActive('blockquote'),
      codeBlock: instance.isActive('codeBlock'),
      link: instance.isActive('link'),
      canUndo: instance.can().undo(),
      canRedo: instance.can().redo(),
    }),
  });

  const headingItems: MenuProps['items'] = [1, 2, 3].map((level) => ({
    key: String(level),
    label: `H${level}`,
    onClick: () => editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run(),
  }));

  const applyLink = () => {
    const typed = linkUrl.trim();
    const chain = editor.chain().focus().extendMarkRange('link');
    if (!typed) {
      chain.unsetLink().run();
      setLinkOpen(false);
      setLinkUrl('');
      return;
    }
    // A bare domain (`example.com`, `www.example.com/x`) is what people actually type — turn it
    // into an absolute https link instead of refusing it.
    const href = BARE_DOMAIN.test(typed) ? `https://${typed}` : typed;
    if (!SAFE_LINK_SCHEME.test(href)) {
      message.warning(t('editorLinkInvalid'));
      return;
    }
    chain.setLink({ href }).run();
    setLinkOpen(false);
    setLinkUrl('');
  };

  return (
    <div className="editor-toolbar">
      <ToolButton title={t('editorBold')} icon={<BoldOutlined />} active={state.bold} onClick={() => editor.chain().focus().toggleBold().run()} />
      <ToolButton title={t('editorItalic')} icon={<ItalicOutlined />} active={state.italic} onClick={() => editor.chain().focus().toggleItalic().run()} />
      <ToolButton title={t('editorUnderline')} icon={<UnderlineOutlined />} active={state.underline} onClick={() => editor.chain().focus().toggleUnderline().run()} />
      <ToolButton title={t('editorStrike')} icon={<StrikethroughOutlined />} active={state.strike} onClick={() => editor.chain().focus().toggleStrike().run()} />
      <ToolButton title={t('editorCode')} icon={<CodeOutlined />} active={state.code} onClick={() => editor.chain().focus().toggleCode().run()} />

      <Divider type="vertical" />

      <Dropdown menu={{ items: headingItems }} trigger={['click']}>
        <span>
          <ToolButton title={t('editorHeading')} icon={<FontSizeOutlined />} active={state.heading} />
        </span>
      </Dropdown>
      <ToolButton title={t('editorBulletList')} icon={<UnorderedListOutlined />} active={state.bulletList} onClick={() => editor.chain().focus().toggleBulletList().run()} />
      <ToolButton title={t('editorOrderedList')} icon={<OrderedListOutlined />} active={state.orderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
      <ToolButton title={t('editorTaskList')} icon={<CheckSquareOutlined />} active={state.taskList} onClick={() => editor.chain().focus().toggleTaskList().run()} />

      <Divider type="vertical" />

      <ToolButton
        title={t('editorQuote')}
        icon={<span className="editor-toolbar-glyph">&ldquo;</span>}
        active={state.blockquote}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <ToolButton
        title={t('editorCodeBlock')}
        icon={<span className="editor-toolbar-glyph">{'{}'}</span>}
        active={state.codeBlock}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />
      <ToolButton
        title={t('editorTable')}
        icon={<TableOutlined />}
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      />

      <Divider type="vertical" />

      <Popover
        open={linkOpen}
        trigger="click"
        placement="bottom"
        onOpenChange={(open) => {
          setLinkOpen(open);
          if (open) {
            setLinkUrl(editor.getAttributes('link').href ?? '');
          }
        }}
        content={
          <Space.Compact style={{ width: 260 }}>
            <Input
              autoFocus
              value={linkUrl}
              placeholder="https://"
              onChange={(event) => setLinkUrl(event.target.value)}
              onPressEnter={applyLink}
            />
            <Button type="primary" onClick={applyLink}>
              OK
            </Button>
          </Space.Compact>
        }
      >
        <span>
          <ToolButton title={t('editorLink')} icon={<LinkOutlined />} active={state.link} />
        </span>
      </Popover>
      <ToolButton title={t('editorImage')} icon={<PictureOutlined />} onClick={onPickImage} />

      <Divider type="vertical" />

      <ToolButton title={t('editorUndo')} icon={<UndoOutlined />} disabled={!state.canUndo} onClick={() => editor.chain().focus().undo().run()} />
      <ToolButton title={t('editorRedo')} icon={<RedoOutlined />} disabled={!state.canRedo} onClick={() => editor.chain().focus().redo().run()} />
    </div>
  );
};

export default EditorToolbar;
